import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { NAV_CATEGORIES, getServiceGuide } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { serviceTitle, serviceDesc, serviceStartingPrice } from '../i18n/serviceCopy';

const categoryHref = (cat, code) =>
  cat === 'INCOME_TAX' ? `/file-itr?service=${code}` : `/get-started?service=${code}`;

const toneClass = (category, isActive) => {
  const base = isActive ? 'ring-1 ring-offset-1' : '';
  switch (category) {
    case 'INCOME_TAX': return `${base} bg-teal-100 border-teal-300 text-teal-900`;
    case 'GST': return `${base} bg-sky-100 border-sky-300 text-sky-900`;
    case 'COMPANY': return `${base} bg-violet-100 border-violet-300 text-violet-900`;
    case 'MCA': return `${base} bg-indigo-100 border-indigo-300 text-indigo-900`;
    case 'ACCOUNTING': return `${base} bg-emerald-100 border-emerald-300 text-emerald-900`;
    case 'PAYROLL': return `${base} bg-amber-100 border-amber-300 text-amber-900`;
    case 'TRADEMARK': return `${base} bg-rose-100 border-rose-300 text-rose-900`;
    default: return `${base} bg-slate-100 border-slate-300 text-slate-900`;
  }
};

const AllServices = () => {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('INCOME_TAX');

  const buttons = useMemo(
    () => NAV_CATEGORIES.map((cat) => ({
      category: cat.category,
      label: cat.category === 'INCOME_TAX'
        ? t('sf.top_cat_itr', 'ITR')
        : cat.category === 'GST'
          ? t('sf.top_cat_gst', 'GST & Filing')
          : t(`category.${cat.category}`, cat.label),
      count: cat.services.length,
      services: cat.services,
    })),
    [t],
  );

  const active = buttons.find((b) => b.category === activeCategory) || buttons[0];

  return (
    <div className="pt-24 pb-20 hero-gradient min-h-screen">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-6">
          <h1 className="ui-title-lg mb-3">{t('sf.multi_title', 'You can request multiple services')}</h1>
          <p className="text-sm text-gray-600">{t('sf.select_multi', 'Select multiple if needed')}</p>
        </div>

        <div className="mb-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 px-0.5">
          {buttons.map((item) => (
            <button
              key={item.category}
              type="button"
              onClick={() => setActiveCategory(item.category)}
              className={`rounded-full border px-3 py-2 text-xs sm:text-sm font-semibold transition-all ${toneClass(item.category, activeCategory === item.category)}`}
            >
              {item.label} ({item.count})
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {active.services.map((svc) => {
            const guide = getServiceGuide(svc.code);
            return (
              <Link key={svc.code} to={categoryHref(svc.category, svc.code)} className="card-lift group p-5 rounded-2xl border border-gray-100 bg-white">
                <h3 className="font-bold text-[#0f2540] mb-1 leading-snug">{serviceTitle(svc.code, t, svc.title)}</h3>
                <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{serviceDesc(svc.code, t, svc.desc)}</p>
                <div className="mt-2 inline-flex items-center rounded-full border border-[#14A098]/25 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-[#0f2540]">
                  {serviceStartingPrice(svc.code, t, guide?.startingPrice)}
                </div>
                <div className="mt-3">
                  <span className="inline-flex items-center gap-1 rounded-full border border-[#14A098] bg-[#14A098] px-3 py-1.5 text-[11px] sm:text-xs font-semibold text-white shadow-sm whitespace-normal text-center leading-snug">
                    {t('common.get_my_service_done', 'Get my {service} done', { service: serviceTitle(svc.code, t, svc.title) })}{' '}
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AllServices;
